import type { LayerStats, LayerValue } from "../types/api";

type Row = LayerValue | LayerStats;

function fmt(n: number | null | undefined, digits = 2) {
  return n == null ? "—" : n.toFixed(digits);
}

function isStats(r: Row): r is LayerStats {
  return "mean" in r;
}

function hasCI(r: Row): boolean {
  return r.q05 != null || r.q95 != null;
}

function ciLabel(r: Row): string {
  return `90% PI: ${fmt(r.q05)} – ${fmt(r.q95)}`;
}

export function LayerTable({ title, rows }: { title: string; rows: Row[] }) {
  if (!rows.length) {
    return (
      <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
        <h3 className="mef-caption">{title}</h3>
        <p className="mt-2 text-[13px] leading-4 text-black/40">No layers ingested yet.</p>
      </div>
    );
  }
  const showStats = rows.some(isStats);
  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <h3 className="mef-caption">{title}</h3>
      <table className="mt-3 w-full text-[13px]">
        <thead>
          <tr className="text-left mef-small-caption">
            <th className="py-1 font-medium">Layer</th>
            {showStats ? (
              <>
                <th className="py-1 text-right font-medium">min</th>
                <th className="py-1 text-right font-medium">mean</th>
                <th className="py-1 text-right font-medium">max</th>
              </>
            ) : (
              <th className="py-1 text-right font-medium">value</th>
            )}
            <th className="py-1 pl-3 font-medium">unit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-t border-mef-surface-2">
              <td className="py-1.5 align-top font-mono text-black/80">{r.code}</td>
              {isStats(r) ? (
                <>
                  <td className="py-1.5 align-top text-right tabular-nums text-black/80">{fmt(r.min)}</td>
                  <td className="py-1.5 align-top text-right tabular-nums text-black/80">
                    <div>{fmt(r.mean)}</div>
                    {hasCI(r) && (
                      <div className="text-[11px] font-normal text-black/40" title={ciLabel(r)}>
                        [{fmt(r.q05)}–{fmt(r.q95)}]
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 align-top text-right tabular-nums text-black/80">{fmt(r.max)}</td>
                </>
              ) : (
                <td className="py-1.5 align-top text-right tabular-nums text-black/80">
                  <div>{fmt(r.value)}</div>
                  {hasCI(r) && (
                    <div className="text-[11px] font-normal text-black/40" title={ciLabel(r)}>
                      [{fmt(r.q05)}–{fmt(r.q95)}]
                    </div>
                  )}
                </td>
              )}
              <td className="py-1.5 align-top pl-3 text-black/40">{r.unit ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
